// src/pages/hotel-bookings/components/CancelHotelBookingDialog.tsx
//
// Admin cancellation of a hotel booking. The cancel goes to the supplier and,
// once confirmed, refunds the customer's wallet automatically — so it asks for
// a reason (sent to the supplier as remarks) and for the booking code typed out.

import { useEffect, useState } from "react";
import { FiAlertTriangle, FiX } from "react-icons/fi";

import type { AdminHotelCancellationOverview } from "@/api/features/hotel-bookings";

import { formatMoney } from "../utils/hotelBookingFormat";

const REASON_MIN = 3;
const REASON_MAX = 500;

export default function CancelHotelBookingDialog({
  open,
  overview,
  isPending,
  errorMessage,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  overview: AdminHotelCancellationOverview;
  isPending: boolean;
  errorMessage: string | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");

  // Never carry a previous confirmation into the next opening.
  useEffect(() => {
    if (open) {
      setReason("");
      setTyped("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isPending) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, isPending, onCancel]);

  if (!open) return null;

  const trimmedReason = reason.trim();
  const reasonOk = trimmedReason.length >= REASON_MIN && trimmedReason.length <= REASON_MAX;
  const codeOk = typed.trim().toUpperCase() === overview.bookingCode.trim().toUpperCase();
  const canSubmit = reasonOk && codeOk && !isPending;
  const currency = overview.currency;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="cancel-hotel-title">
      <div className="absolute inset-0 bg-black/30" onClick={() => !isPending && onCancel()} />
      <div className="absolute inset-0 grid place-items-center p-4">
        <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h3 id="cancel-hotel-title" className="text-sm font-semibold text-gray-900">
              Cancel hotel booking {overview.bookingCode}
            </h3>
            <button
              className="grid h-8 w-8 place-items-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-50"
              onClick={onCancel}
              disabled={isPending}
              aria-label="Close"
            >
              <FiX />
            </button>
          </div>

          <div className="space-y-4 p-4 sm:p-5">
            <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
              <FiAlertTriangle className="mt-0.5 shrink-0" aria-hidden />
              <p>
                This sends a cancellation to the supplier and cannot be undone. Once the supplier
                confirms, the refund is credited to the customer's wallet automatically.
              </p>
            </div>

            {overview.estimate ? (
              <dl className="space-y-1 rounded-lg border border-gray-200 px-3 py-2.5 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500">Paid</dt>
                  <dd className="text-gray-900">{formatMoney(overview.paidAmount, currency)}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500">Hotel cancellation charge (est.)</dt>
                  <dd className="text-gray-900">
                    − {formatMoney(overview.estimate.cancellationCharge, currency)}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500">Convenience fee + GST (kept)</dt>
                  <dd className="text-gray-900">− {formatMoney(overview.estimate.serviceFee, currency)}</dd>
                </div>
                <div className="flex justify-between gap-4 border-t border-gray-100 pt-1.5 font-semibold">
                  <dt className="text-gray-900">Estimated refund</dt>
                  <dd className="text-gray-900">{formatMoney(overview.estimate.refundAmount, currency)}</dd>
                </div>
                <p className="pt-1 text-xs text-gray-500">
                  Based on the policy saved at booking. The supplier's own figure is final.
                </p>
              </dl>
            ) : null}

            <label className="grid gap-1 text-sm">
              <span className="text-gray-700">Reason (sent to the supplier)</span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={REASON_MAX}
                rows={3}
                disabled={isPending}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                placeholder="e.g. Guest requested cancellation by phone"
              />
            </label>

            <label className="grid gap-1 text-sm">
              <span className="text-gray-700">
                Type <strong className="font-semibold">{overview.bookingCode}</strong> to confirm
              </span>
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                disabled={isPending}
                autoComplete="off"
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
              />
            </label>

            {errorMessage ? (
              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                {errorMessage}
              </p>
            ) : null}
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
            <button
              onClick={onCancel}
              disabled={isPending}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Keep booking
            </button>
            <button
              onClick={() => onConfirm(trimmedReason)}
              disabled={!canSubmit}
              className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:bg-red-300"
            >
              {isPending ? "Cancelling…" : "Cancel booking"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
